import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, Send, Eye, Clock, Sparkles } from "lucide-react";
import AiCenterReview from "@/components/AiCenterReview";

export default function DailyReportPanel({ api }) {
  const [daily, setDaily] = useState(null);
  const [sending, setSending] = useState(false);
  const [reportTime, setReportTime] = useState("08:30");

  const loadDaily = useCallback(async () => {
    try { const r = await axios.get(`${api}/reports/preview`); setDaily(r.data); } catch (e) { toast.error("Failed to load report preview"); }
  }, [api]);
  const loadSettings = useCallback(async () => {
    try { const r = await axios.get(`${api}/settings`); setReportTime(r.data.report_time || "08:30"); } catch (e) { toast.error("Failed to load report settings"); }
  }, [api]);
  useEffect(() => { loadDaily(); loadSettings(); }, [loadDaily, loadSettings]);

  const saveReportTime = async () => {
    try {
      const r = await axios.put(`${api}/settings/report-time`, { time: reportTime });
      setReportTime(r.data.report_time);
      toast.success(`Daily digest set to ${r.data.report_time} (America/Toronto, Mon–Fri)`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save time");
    }
  };

  const sendDaily = async () => {
    setSending(true);
    try { const r = await axios.post(`${api}/reports/send-daily`); toast.success(`Daily digest sent to ${r.data.recipient}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  return (
    <Tabs defaultValue="daily">
      <TabsList className="bg-white border border-slate-200 p-1 rounded-lg mb-4" data-testid="report-subtabs">
        <TabsTrigger value="daily" data-testid="tab-daily-report" className="data-[state=active]:bg-[#478261] data-[state=active]:text-white">
          <Clock className="w-4 h-4 mr-2" />Daily Digest
        </TabsTrigger>
        <TabsTrigger value="ai" data-testid="tab-ai-review" className="data-[state=active]:bg-[#478261] data-[state=active]:text-white">
          <Sparkles className="w-4 h-4 mr-2" />AI Review
        </TabsTrigger>
      </TabsList>

      <TabsContent value="daily">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1 border-slate-200 shadow-sm h-fit">
            <CardHeader className="border-b border-slate-100">
              <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Schedule</div>
              <CardTitle className="text-lg">Daily Digest</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4 text-sm">
              <div className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-slate-500" /><div><div className="text-xs uppercase tracking-wider text-slate-500">Recipient</div><div className="font-mono text-slate-900">{daily?.recipient || "d.viola@workplaceone.com"}</div></div></div>
              <div className="flex items-start gap-3"><Clock className="w-4 h-4 mt-0.5 text-slate-500" /><div className="flex-1"><div className="text-xs uppercase tracking-wider text-slate-500">Delivery · Mon–Fri · Eastern Time (New York)</div>
                <div className="flex gap-2 mt-1.5">
                  <Input type="time" value={reportTime} onChange={(e) => setReportTime(e.target.value)} className="bg-white w-[130px]" data-testid="report-time-input" />
                  <Button variant="outline" size="sm" onClick={saveReportTime} className="bg-white" data-testid="report-time-save-button">Save</Button>
                </div>
              </div></div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">Includes all <strong>outstanding</strong> and <strong>overdue</strong> orders grouped by property.</div>
              <div className="pt-2 border-t border-slate-100 space-y-2">
                <Button onClick={loadDaily} variant="outline" className="w-full" data-testid="refresh-daily-preview-button"><Eye className="w-4 h-4 mr-2" />Refresh Preview</Button>
                <Button onClick={sendDaily} disabled={sending} className="w-full bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="send-daily-report-now-button"><Send className="w-4 h-4 mr-2" />{sending ? "Sending..." : "Send Test Report Now"}</Button>
              </div>
            </CardContent>
          </Card>
          <Card className="lg:col-span-2 border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
              <div><div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Preview</div><CardTitle className="text-lg">08:30 AM Digest</CardTitle></div>
              {daily && <div className="text-xs font-mono text-slate-500">{daily.count} open · {daily.date}</div>}
            </CardHeader>
            <CardContent className="p-0">
              {daily ? <iframe title="Daily Report" srcDoc={daily.html} className="w-full border-0" style={{ height: 640 }} data-testid="daily-preview-iframe" /> : <div className="p-6 text-sm text-slate-500">Loading preview...</div>}
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="ai">
        <AiCenterReview api={api} />
      </TabsContent>
    </Tabs>
  );
}
